<template>
  <div v-if="show" class="modal fade show" style="display: block; z-index: 1060">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-body">
          <Icon @click="closeModal" icon="mdi:close" class="close-icon" width="20" height="20" />
          <div class="row">
            <div class="col-2" style="padding: 0px">
              <Icon icon="mdi:phone-in-talk" class="col-12 modal-icon" width="48" height="48" />
            </div>
            <div class="col-10">
              <h6 class="title">Live Conversation Detected</h6>
              <small class="sub-title">
                You had a live conversation with <strong>{{ contactName }}</strong
                >. Would you like to create a follow-up task?
              </small>
              <div v-if="duration" class="call-details">
                <small class="detail-text">
                  <Icon icon="mdi:clock-outline" width="14" height="14" />
                  Duration: {{ formatDuration(duration) }}
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="footer">
          <div class="row">
            <div class="col-6">
              <Button variant="secondary" size="md" block :disabled="isLoading" @click="closeModal">
                Dismiss
              </Button>
            </div>
            <div class="col-6">
              <Button
                variant="primary"
                size="md"
                block
                :loading="isLoading"
                :disabled="isLoading"
                @click="createTask"
              >
                Create Task
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { Button } from "@/components/ui";
import { useRouter } from "vue-router";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";

interface Props {
  show: boolean;
  contactId: string;
  contactName: string;
  callLogId: string;
  duration: number | null;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const emit = defineEmits<{
  close: [];
  create: [callLogId: string, contactId: string];
}>();

const router = useRouter();

const closeModal = () => {
  emit("close");
};

const createTask = () => {
  emit("create", props.callLogId, props.contactId);
  // Navigate to contact overview where user can create a bucket
  // The contact overview page can check for callLogId in query to pre-fill bucket creation
  router.push({
    name: CONTACT_OVERVIEW_ROUTE,
    params: { id: props.contactId },
    query: {
      createBucket: "true",
      callLogId: props.callLogId,
      taskType: "call",
    },
  });
  closeModal();
};

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return "N/A";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};
</script>

<style scoped>
.modal-content {
  border-radius: 20px !important;
  border: 1px solid transparent;
  background-color: #fdfdfe !important;
  max-height: 92vh;
  overflow-x: hidden;
  overflow-y: auto;
}

.modal {
  display: none;
}

.modal.show {
  display: block;
}

.modal-dialog {
  max-width: 35%;
  z-index: 10020 !important;
}

.modal {
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 10019 !important;
}

.modal-body {
  padding-top: 38px;
}

.modal-body .title {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 4px;
  color: #333b69;
}

.modal-body .sub-title {
  font-weight: 400;
  font-size: 0.7rem;
  color: #6f6c90;
  line-height: 1.4;
}

.modal-body .sub-title strong {
  font-weight: 600;
  color: #333b69;
}

.call-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e4e4e7;
}

.detail-text {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #6f6c90;
  font-size: 0.65rem;
}

.modal-content {
  border: 1px solid #e4e4e7;
}

.close-icon {
  height: 20px;
  width: 20px;
  position: absolute;
  top: 12px;
  right: 15px;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s ease;
}

.close-icon:hover {
  color: #374151;
}

.footer {
  border-top: 1px solid transparent;
  padding: 0px 18px 18px 18px;
  width: 100%;
  margin-top: 10px;
}

.modal-icon {
  width: 90%;
  margin-left: 10px;
  color: #3e3efe;
}
</style>
